/**
 * Caixa depositado mês a mês (série mensal de recebidos).
 */
import { useMemo, useState } from 'react'
import { formatCurrency, formatNumber } from '../../../../utils/obraAnalytics'

const RecebidosMensalPanel = ({ title = 'Caixa depositado por mês', serie = [] }) => {
    const [hover, setHover] = useState(null)

    const meses = useMemo(() => (serie || []).map((r) => ({
        mes_ano: r.mes_ano,
        label: r.label ?? r.mes_ano,
        depositado: Number(r.depositado || r.total || 0),
        quantidade: Number(r.quantidade || 0),
    })), [serie])

    const max = Math.max(...meses.map((m) => m.depositado), 1)
    const temDeposito = meses.some((m) => m.depositado > 0)
    const active = hover != null ? meses[hover] : null

    if (!meses.length) {
        return (
            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                <p className="text-xs text-gray-400 mt-2">Sem meses no período.</p>
            </div>
        )
    }

    return (
        <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-4 min-h-[56px]">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
                    <p className="text-xs text-gray-400 mt-1">Por data de compensação</p>
                </div>
                {active && (
                    <div className="text-right text-xs">
                        <p className="font-bold text-gray-800">{active.label}</p>
                        <p className="text-cyan-700 font-semibold">{formatCurrency(active.depositado)}</p>
                        {active.quantidade > 0 && (
                            <p className="text-gray-400">{formatNumber(active.quantidade)} recebimento(s)</p>
                        )}
                    </div>
                )}
            </div>

            {!temDeposito ? (
                <div className="flex items-center justify-center h-40">
                    <p className="text-xs text-gray-400">Nenhum depósito no período.</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <div
                        className="flex items-end gap-2 h-48 px-1"
                        style={{ minWidth: `${Math.max(meses.length * 48, 280)}px` }}
                    >
                        {meses.map((m, idx) => {
                            const pct = m.depositado > 0 ? Math.max(8, Math.round((m.depositado / max) * 100)) : 0
                            const ativo = hover === idx
                            return (
                                <div
                                    key={m.mes_ano || idx}
                                    className="flex flex-col items-center justify-end h-full flex-1 min-w-[36px] max-w-[64px]"
                                    onMouseEnter={() => setHover(idx)}
                                    onMouseLeave={() => setHover(null)}
                                    title={`${m.label}: ${formatCurrency(m.depositado)}`}
                                >
                                    <div
                                        className={`w-full rounded-t-md transition-all ${m.depositado > 0 ? 'bg-cyan-500' : 'bg-gray-100'} ${ativo ? 'ring-2 ring-cyan-300' : ''}`}
                                        style={{ height: m.depositado > 0 ? `${pct}%` : '2px' }}
                                    />
                                    <span className={`text-[10px] mt-1 leading-tight text-center w-full truncate ${ativo ? 'text-cyan-700 font-bold' : 'text-gray-400'}`}>
                                        {m.label}
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default RecebidosMensalPanel
